import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useSupplierCrmOrgStore } from '../../../store/supplierCrmOrg.store';
import contactsService, { SupplierContact, SupplierContactRole } from '../../../services/supplierCrm/contactsService';
import suppliersService, { Supplier } from '../../../services/supplierCrm/suppliersService';
import SupplierContactsDrawer from '../../supplier-crm/SupplierContactsDrawer';

const ROLE_OPTIONS: Array<{ value: SupplierContactRole | 'all'; label: string }> = [
  { value: 'all', label: 'All roles' },
  { value: 'account_manager', label: 'Account Manager' },
  { value: 'credit_control', label: 'Credit Control' },
  { value: 'logistics', label: 'Logistics' },
  { value: 'executive', label: 'Executive' },
  { value: 'operations', label: 'Operations' },
  { value: 'other', label: 'Other' }
];

const ContactsTab: React.FC = () => {
  const { activeOrgId } = useSupplierCrmOrgStore();
  const [items, setItems] = useState<SupplierContact[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<SupplierContactRole | 'all'>('all');
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);

  const supplierMap = useMemo(() => (
    suppliers.reduce<Record<string, Supplier>>((acc, supplier) => {
      acc[supplier.id] = supplier;
      return acc;
    }, {})
  ), [suppliers]);

  const fetchSuppliers = useCallback(async () => {
    if (!activeOrgId) return;
    try {
      const { data } = await suppliersService.list({
        organization_id: activeOrgId,
        limit: 200,
        offset: 0
      });
      setSuppliers(data || []);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to load suppliers');
    }
  }, [activeOrgId]);

  const fetchContacts = useCallback(async () => {
    if (!activeOrgId) return;

    setLoading(true);
    try {
      const params: any = {
        organization_id: activeOrgId,
        limit: 100,
        offset: 0,
        search: search.trim() || undefined
      };
      if (roleFilter !== 'all') {
        params.role = roleFilter;
      }

      const { data } = await contactsService.list(params);
      setItems(data || []);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to load contacts');
    } finally {
      setLoading(false);
    }
  }, [activeOrgId, roleFilter, search]);

  useEffect(() => {
    void fetchSuppliers();
  }, [fetchSuppliers]);

  useEffect(() => {
    void fetchContacts();
  }, [fetchContacts]);

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="flex items-center gap-3">
        <input
          type="text"
          className="flex-1 rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
          placeholder="Search contacts by name, email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          className="rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-primary focus:outline-none"
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value as SupplierContactRole | 'all')}
        >
          {ROLE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      {/* Contacts Table */}
      <div className="rounded-xl bg-white p-6 shadow-sm ring-1 ring-gray-100">
        {loading ? (
          <div className="py-10 text-center text-sm text-gray-500">Loading contacts...</div>
        ) : items.length === 0 ? (
          <div className="py-10 text-center text-sm text-gray-500">
            No contacts found. Add contacts from the Suppliers tab.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Contact</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Role</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Supplier</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Email</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Phone</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Primary</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((contact) => {
                  const supplier = supplierMap[contact.supplier_id];
                  return (
                    <tr key={contact.id} className="transition hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="font-semibold text-gray-900">{contact.full_name}</div>
                        {contact.job_title && (
                          <div className="text-xs text-gray-500">{contact.job_title}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        {ROLE_OPTIONS.find(r => r.value === contact.role)?.label || contact.role}
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        {supplier ? supplier.name : 'Unknown'}
                      </td>
                      <td className="px-4 py-3 text-gray-600">{contact.email || '—'}</td>
                      <td className="px-4 py-3 text-gray-600">{contact.phone || '—'}</td>
                      <td className="px-4 py-3">
                        {contact.is_primary ? (
                          <span className="inline-flex items-center rounded-full bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary">
                            ⭐ Primary
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {supplier && (
                          <button
                            onClick={() => setSelectedSupplier(supplier)}
                            className="rounded-lg border border-primary/30 px-3 py-1.5 text-xs font-semibold text-primary transition hover:bg-primary/10"
                          >
                            View Supplier
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Supplier Drawer */}
      <SupplierContactsDrawer
        isOpen={selectedSupplier != null}
        supplier={selectedSupplier}
        organizationId={selectedSupplier ? activeOrgId : null}
        onClose={() => setSelectedSupplier(null)}
        onContactsChanged={() => void fetchContacts()}
      />
    </div>
  );
};

export default ContactsTab;
